import { useMemo } from "react";
import { useNavigate } from "react-router";

import {
  CommandPaletteBucket,
  CommandPaletteAction,
  CommandPaletteRecentAction,
  CommandPaletteActionType,
} from "../types";
import { BUCKET_IDS } from "../constants";
import useCommandPaletteRecentActionsStore from "../store/useRecentActionsStore";
import { selectTopRecentActions } from "../store/useRecentActionsStore/selectors";
import { deconstructRecentActionId } from "../utils/ids";
import restoreSubListItemData from "../utils/restoreSubListItemData";

const useCommandPaletteRecentActionsBucket = (
  otherBuckets: CommandPaletteBucket[]
): CommandPaletteBucket => {
  const navigate = useNavigate();

  const storedActions = useCommandPaletteRecentActionsStore(selectTopRecentActions);

  const actionsMap = useMemo(() => {
    const allActions = otherBuckets.flatMap((bucket) => bucket.getActions());
    return new Map(allActions.map((action) => [action.id, action]));
  }, [otherBuckets]);

  return useMemo(() => {
    const reconstructAction = (
      storedAction: CommandPaletteRecentAction
    ): CommandPaletteAction | undefined => {
      if (storedAction.type === CommandPaletteActionType.SIMPLE) {
        const originalAction = actionsMap.get(deconstructRecentActionId(storedAction.id));
        if (!originalAction) return undefined;

        return {
          ...originalAction,
          id: storedAction.id,
          bucketId: BUCKET_IDS.RECENT_ACTIONS,
          alwaysVisible: false,
        };
      }

      const { metadata } = storedAction;
      if (!metadata?.navigationPath) return undefined;

      const { icon, title } = restoreSubListItemData(metadata.navigationPath);

      return {
        ...storedAction,
        metadata: {
          title,
        },
        bucketId: BUCKET_IDS.RECENT_ACTIONS,
        icon,
        onAction: () => navigate(metadata.navigationPath!),
      };
    };

    const actions = storedActions.map(reconstructAction).filter((action) => action !== undefined);

    return {
      id: BUCKET_IDS.RECENT_ACTIONS,
      title: "Recent",
      order: 0,
      getActions: () => actions,
    };
  }, [storedActions, actionsMap, navigate]);
};

export default useCommandPaletteRecentActionsBucket;
